/* Internal, consent-gated editor funnel. No design data, cookies or persistent identifiers. */
(() => {
  'use strict';
  try {
    const script = document.currentScript;
    const endpoint = 'https://netthier-warteliste.bh-printdesigns.workers.dev/v1/editor-events';
    if (script && script.dataset.enabled === 'false') return;
    const names = new Set(['editor_ready', 'design_started', 'preview_open', 'preview_ready', 'preview_error', 'selector_open', 'selection_changed', 'cart_intent', 'export_started', 'export_ready', 'cart_add_started', 'cart_add_success', 'cart_error']);
    const stages = new Set(['preview', 'selection', 'export', 'cart']);
    const errors = new Set(['preview_failed', 'export_failed', 'upload_failed', 'prices_unavailable', 'cart_rejected', 'cart_timeout', 'cart_network', 'no_selection', 'unknown']);
    const attempted = new Set(['cart_intent', 'export_started', 'export_ready', 'cart_add_started', 'cart_add_success', 'cart_error']);
    const idleMs = 30 * 60 * 1000;
    let ready = false, resolved = false, denied = false, missed = false;
    let session = null, attempt = null, queue = [], timer = null, inFlight = false, epoch = 0;
    if (script) script.dataset.funnelState = 'awaiting-consent';
    const safely = (fn) => (...args) => { try { return fn(...args); } catch (_) { return undefined; } };
    function allowed() {
      try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; }
      catch (_) { return false; }
    }
    function uuid() {
      if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 15) | 64;
      bytes[8] = (bytes[8] & 63) | 128;
      const h = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
      return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
    }
    function environment() {
      try {
        const q = new URLSearchParams(window.location.search);
        const role = script?.dataset.themeRole || window.Shopify?.theme?.role;
        return window.Shopify?.designMode || (role && role !== 'main' && role !== 'live') ||
          q.has('nh_funnel_test') || q.has('preview_theme_id') || q.has('audit-products') || q.has('nh_debug_progress') ||
          !['netthieraufkleber.de', 'www.netthieraufkleber.de'].includes(window.location.hostname) ? 'test' : 'live';
      } catch (_) { return 'test'; }
    }
    function attributes(partial) {
      const width = window.innerWidth || 1024;
      // Only a coarse family is retained; the original user agent is never sent.
      const ua = window.navigator?.userAgent || '';
      const browser = /Edg(?:e|A|iOS)?\//.test(ua) ? 'edge' : /Firefox\/|FxiOS\//.test(ua) ? 'firefox' :
        /Chrome\/|CriOS\//.test(ua) ? 'chrome' : /Safari\//.test(ua) ? 'safari' : 'other';
      return { device: width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop', browser, partial, mode: environment() };
    }
    function clear() {
      epoch++;
      if (timer !== null) window.clearTimeout(timer);
      timer = null; queue = []; session = null; attempt = null;
    }
    function gate() {
      if (allowed()) { if (script) script.dataset.funnelState = 'active'; return true; }
      if (script) script.dataset.funnelState = 'awaiting-consent';
      if (session || queue.length) clear();
      return false;
    }
    function schedule(delay = 500) {
      if (timer !== null || inFlight || !queue.length) return;
      timer = window.setTimeout(safely(() => { timer = null; void flush(); }), delay);
    }
    function enqueue(name, meta = {}) {
      if (!session || session.seq >= 256) return;
      if (attempted.has(name) && !attempt) return;
      if (['cart_add_success', 'cart_error'].includes(name)) {
        if (attempt.terminal) return;
        attempt.terminal = name;
      }
      const seen = attempted.has(name) ? attempt.seen : session.seen;
      if (seen.has(name)) return;
      seen.add(name);
      const event = { id: uuid(), sid: session.id, seq: ++session.seq, name, ...session.attrs };
      if (attempted.has(name)) event.aid = attempt.id;
      if (stages.has(meta.stage)) event.stage = meta.stage;
      else if (attempted.has(name) && stages.has(attempt.stage)) event.stage = attempt.stage;
      if (errors.has(meta.error)) event.error = meta.error;
      if (attempted.has(name) && ['export_ready', 'cart_add_success', 'cart_error'].includes(name)) {
        const ms = Math.max(0, Date.now() - attempt.startedAt);
        event.duration = ms < 2000 ? 'under_2s' : ms < 5000 ? '2_5s' : ms < 15000 ? '5_15s' : ms < 30000 ? '15_30s' : ms < 60000 ? '30_60s' : 'over_60s';
      }
      if (queue.length < 80) queue.push({ event, retries: 0 });
      schedule();
    }
    function ensureSession() {
      const now = Date.now();
      if (session && now - session.lastAt >= idleMs && !attempt) {
        // Flush the old visit without claiming that it was abandoned.
        void flush(); session = null;
      }
      if (!session) {
        session = { id: uuid(), seq: 0, lastAt: now, attrs: attributes(denied || missed), seen: new Set() };
        denied = false; missed = false;
      }
      session.lastAt = now;
      if (ready) enqueue('editor_ready');
    }
    function emit(name, meta) {
      if (!names.has(name)) return;
      if (!gate()) { if (name !== 'editor_ready') missed = true; return; }
      ensureSession();
      enqueue(name, meta);
    }
    async function flush() {
      if (inFlight || !queue.length || !gate()) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      const currentEpoch = epoch, batch = queue.splice(0, 20);
      inFlight = true;
      let timeout = null, retry = false;
      try {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        if (controller) timeout = window.setTimeout(() => controller.abort(), 4000);
        const response = await window.fetch(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ v: 1, events: batch.map(item => item.event) }),
          mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer', keepalive: true,
          ...(controller ? { signal: controller.signal } : {})
        });
        if (script) script.dataset.funnelLastStatus = String(response.status);
        retry = response.status >= 500;
      } catch (_) { retry = true; }
      finally {
        if (timeout !== null) window.clearTimeout(timeout);
        inFlight = false;
        if (allowed()) {
          if (currentEpoch === epoch && retry) queue = batch.filter(item => item.retries < 1).map(item => ({ event: item.event, retries: item.retries + 1 })).concat(queue).slice(0, 80);
          schedule(retry ? 2000 : 500);
        }
      }
    }
    function reconcile() {
      const can = allowed();
      if (script) script.dataset.funnelState = can ? 'active' : 'awaiting-consent';
      if (!can) { if (resolved) denied = true; clear(); }
      else if (ready) ensureSession();
      resolved = true;
    }
    window.NH_EditorFunnel = Object.freeze({
      ready: safely(() => { ready = true; reconcile(); }),
      emit: safely(emit),
      startAttempt: safely(() => {
        if (!gate()) { missed = true; return; }
        ensureSession();
        attempt = { id: uuid(), startedAt: Date.now(), stage: 'selection', seen: new Set() };
        enqueue('cart_intent');
      }),
      setStage: safely(stage => { if (attempt && stages.has(stage)) attempt.stage = stage; }),
      endAttempt: safely(() => { attempt = null; }),
    });
    document.addEventListener('visitorConsentCollected', safely(() => { denied = true; reconcile(); }));
    document.addEventListener('visibilitychange', safely(() => { if (document.visibilityState === 'hidden') void flush(); }));
    window.addEventListener('pagehide', safely(() => { void flush(); }));
    // The initial permission must be checked: an existing decision need not emit an event.
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') reconcile();
    else if (typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], safely(error => {
        if (!error) reconcile();
      }));
    }
  } catch (_) { /* Measurement must never affect editing or ordering. */ }
})();
